import { useState, useRef, useEffect } from "react";
import { SOURCE_TYPE } from "../utils/dataSource";

const loadParser = () => import("../parsers/altshulerParser");

const fmt = n => (n || 0).toLocaleString("he-IL", { maximumFractionDigits: 0 });

function findByTaz(taz, holdingsClients, clients) {
  if (!taz) return null;
  const searchTaz = String(Number(taz));
  if (searchTaz === "NaN") return null;
  
  const inHoldings = holdingsClients.find(c =>
    c.clientId === taz ||
    (c.clientId && String(Number(c.clientId)) === searchTaz)
  );
  if (inHoldings) return inHoldings;

  const inAdvice = clients.find(c => 
    c.clientId === taz ||
    (c.clientId && String(Number(c.clientId)) === searchTaz)
  );
  if (inAdvice) return { clientId: inAdvice.clientId, clientName: inAdvice.clientName };

  return null;
}

export default function AltshulerImport({ files, onClose, onImport, clients, holdingsClients }) {
  const [busy, setBusy] = useState(false);
  const [parsedData, setParsedData] = useState(null);
  const [selections, setSelections] = useState({}); // idx -> boolean
  const [reportMonth, setReportMonth] = useState(""); // YYYY-MM

  useEffect(() => {
    if (files && files.length > 0) {
      processFile(files[0]);
    }
  }, [files]);

  const processFile = async (file) => {
    setBusy(true);
    try {
      const { parse } = await loadParser();
      const XLSX = await import("xlsx");
      
      const buffer = await file.arrayBuffer();
      const workbook = XLSX.read(buffer, { type: "array" });
      const { accounts } = parse(workbook, file.name);

      const processed = accounts.map((acc, idx) => {
        const existingClient = findByTaz(acc.taz, holdingsClients, clients);
        
        let fundExists = false;
        if (existingClient && existingClient.funds) {
          fundExists = existingClient.funds.some(f => {
             const isAltshuler = f.companyCode === "15" || f.companyName?.includes("אלטשולר");
             if (!isAltshuler) return false;

             const cleanF = String(f.polNum || "").replace(/\D/g, "");
             const cleanA = String(acc.accountNum || "").replace(/\D/g, "");
             const cleanB = String(acc.fundNum || "").replace(/\D/g, "");

             if (cleanF.length > 3) {
               if (cleanA && (cleanA.endsWith(cleanF) || cleanF.endsWith(cleanA))) return true;
               if (cleanB && (cleanB.endsWith(cleanF) || cleanF.endsWith(cleanB))) return true;
             }
             return false;
          });
        }

        return {
          ...acc,
          idx,
          existingClient,
          fundExists
        };
      });

      setParsedData(processed);
      
      // Auto-select those that are not existing funds
      const initialSelections = {};
      processed.forEach(p => {
        if (!p.fundExists) initialSelections[p.idx] = true;
      });
      setSelections(initialSelections);

      // Extract month from filename if possible: e.g. "פירוט עמלות מאי" -> defaults to current month or similar
      // Since it's hard to parse "מאי", we'll just set it to the current month or let the user choose
      const today = new Date();
      setReportMonth(`${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`);

    } catch (err) {
      alert("שגיאה בפענוח קובץ אלטשולר: " + err.message);
      onClose();
    } finally {
      setBusy(false);
    }
  };

  const handleConfirm = () => {
    if (!parsedData) return;
    const toImport = parsedData.filter(p => selections[p.idx]);
    if (!toImport.length) {
      onClose();
      return;
    }

    const funds = toImport.map(p => {
      // Create fund object compatible with addHoldingsFunds
      return {
        id: `ALTSHULER_${p.accountNum || p.fundNum || p.taz}_${Date.now()}`,
        client: { id: p.taz, name: p.clientName || "לקוח אלטשולר" },
        companyCode: "15",
        companyName: "אלטשולר שחם",
        polNum: p.accountNum || p.fundNum || "",
        sugMutzar: p.sugMutzar,
        cat: p.cat,
        statusLabel: "פעיל",
        balance: p.balance,
        sourceType: SOURCE_TYPE.ALTSHULER_EXCEL,
        joinDate: p.joinDate,
        validDate: reportMonth ? `${reportMonth.split('-').reverse().join('/')}` : null,
        maslulim: [{
          name: p.trackName || "כללי",
          trackId: p.trackId,
          balance: p.balance,
        }]
      };
    });

    onImport(funds, { fileName: files[0]?.name });
    onClose();
  };

  const toggleSelection = (idx) => {
    setSelections(prev => ({ ...prev, [idx]: !prev[idx] }));
  };

  if (busy || !parsedData) {
    return (
      <div style={{
        position: "fixed", inset: 0, zIndex: 4000, background: "rgba(15,23,42,0.5)",
        display: "flex", alignItems: "center", justifyContent: "center", direction: "rtl",
      }}>
        <div style={{ background: "white", padding: 32, borderRadius: 16, textAlign: "center" }}>
          <div style={{ fontSize: 40, marginBottom: 16, animation: "spin 1s linear infinite" }}>⏳</div>
          <h3 style={{ margin: 0, color: "#1e293b" }}>מפענח קובץ אלטשולר...</h3>
        </div>
      </div>
    );
  }

  const newClients = parsedData.filter(p => !p.existingClient);
  const existingClientsNewFunds = parsedData.filter(p => p.existingClient && !p.fundExists);
  const existingFunds = parsedData.filter(p => p.fundExists);

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 4000, background: "rgba(15,23,42,0.5)",
      display: "flex", alignItems: "center", justifyContent: "center", direction: "rtl",
    }}>
      <div style={{
        background: "white", borderRadius: 16, width: 800, maxWidth: "90vw", maxHeight: "90vh",
        display: "flex", flexDirection: "column", overflow: "hidden",
        boxShadow: "0 20px 60px rgba(0,0,0,0.25)"
      }}>
        {/* Header */}
        <div style={{ padding: "20px 24px", borderBottom: "1px solid #e2e8f0", display: "flex", justifyContent: "space-between", alignItems: "center", background: "#f8fafc" }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 18, color: "#0f172a" }}>ייבוא קובץ אלטשולר שחם</h2>
            <div style={{ fontSize: 13, color: "#64748b", marginTop: 4 }}>
              {files[0]?.name}
            </div>
          </div>
          <button onClick={onClose} style={{ background: "transparent", border: "none", fontSize: 24, cursor: "pointer", color: "#94a3b8" }}>✕</button>
        </div>

        {/* Content */}
        <div style={{ padding: 24, overflowY: "auto", flex: 1, display: "flex", flexDirection: "column", gap: 24 }}>
          
          <div style={{ display: "flex", alignItems: "center", gap: 12, background: "#f8fafc", padding: "12px 16px", borderRadius: 8, border: "1px solid #e2e8f0" }}>
            <span style={{ fontSize: 14, fontWeight: 600, color: "#334155" }}>חודש שיוך (נכונות נתונים):</span>
            <input 
              type="month" 
              value={reportMonth}
              onChange={(e) => setReportMonth(e.target.value)}
              style={{ fontSize: 14, padding: "6px 12px", border: "1px solid #cbd5e1", borderRadius: 6 }}
            />
          </div>

          <div style={{ display: "flex", gap: 16 }}>
            <div style={{ flex: 1, background: "#f0fdf4", border: "1px solid #bbf7d0", padding: 16, borderRadius: 8 }}>
              <div style={{ fontSize: 12, color: "#166534", fontWeight: 600 }}>לקוחות חדשים שייווצרו</div>
              <div style={{ fontSize: 24, color: "#15803d", fontWeight: 700 }}>{newClients.length}</div>
            </div>
            <div style={{ flex: 1, background: "#fffbeb", border: "1px solid #fef08a", padding: 16, borderRadius: 8 }}>
              <div style={{ fontSize: 12, color: "#854d0e", fontWeight: 600 }}>קופות חדשות ללקוחות קיימים</div>
              <div style={{ fontSize: 24, color: "#a16207", fontWeight: 700 }}>{existingClientsNewFunds.length}</div>
            </div>
            <div style={{ flex: 1, background: "#f1f5f9", border: "1px solid #e2e8f0", padding: 16, borderRadius: 8 }}>
              <div style={{ fontSize: 12, color: "#475569", fontWeight: 600 }}>קופות שכבר קיימות (התעלמות)</div>
              <div style={{ fontSize: 24, color: "#64748b", fontWeight: 700 }}>{existingFunds.length}</div>
            </div>
          </div>

          {(newClients.length > 0 || existingClientsNewFunds.length > 0) && (
            <div>
              <h3 style={{ margin: "0 0 12px 0", fontSize: 14, color: "#334155" }}>פירוט קופות להוספה</h3>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr style={{ background: "#f8fafc", color: "#64748b", textAlign: "right" }}>
                    <th style={{ padding: "8px 12px", borderBottom: "2px solid #e2e8f0" }}>ייבוא</th>
                    <th style={{ padding: "8px 12px", borderBottom: "2px solid #e2e8f0" }}>ת"ז</th>
                    <th style={{ padding: "8px 12px", borderBottom: "2px solid #e2e8f0" }}>שם לקוח</th>
                    <th style={{ padding: "8px 12px", borderBottom: "2px solid #e2e8f0" }}>סוג לקוח</th>
                    <th style={{ padding: "8px 12px", borderBottom: "2px solid #e2e8f0" }}>מספר קופה/חשבון</th>
                    <th style={{ padding: "8px 12px", borderBottom: "2px solid #e2e8f0" }}>מוצר</th>
                    <th style={{ padding: "8px 12px", borderBottom: "2px solid #e2e8f0" }}>יתרה</th>
                  </tr>
                </thead>
                <tbody>
                  {[...newClients, ...existingClientsNewFunds].map((p, i) => (
                    <tr key={i} style={{ borderBottom: "1px solid #f1f5f9", background: p.existingClient ? "transparent" : "#f0fdf4" }}>
                      <td style={{ padding: "8px 12px" }}>
                        <input type="checkbox" checked={!!selections[p.idx]} onChange={() => toggleSelection(p.idx)} />
                      </td>
                      <td style={{ padding: "8px 12px" }}>{p.taz}</td>
                      <td style={{ padding: "8px 12px" }}>{p.clientName}</td>
                      <td style={{ padding: "8px 12px", color: p.existingClient ? "#64748b" : "#166534" }}>
                        {p.existingClient ? "קיים" : "חדש"}
                      </td>
                      <td style={{ padding: "8px 12px" }}>{p.accountNum || p.fundNum}</td>
                      <td style={{ padding: "8px 12px" }}>{p.sugMutzar}</td>
                      <td style={{ padding: "8px 12px", direction: "ltr", textAlign: "right" }}>₪{fmt(p.balance)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          
          {existingFunds.length > 0 && (
            <div style={{ fontSize: 12, color: "#64748b", padding: 12, background: "#f8fafc", borderRadius: 8 }}>
              {existingFunds.length} קופות הוסתרו כיוון שהן זוהו במערכת (לפי מספר חשבון/קופה ות.ז).
            </div>
          )}

        </div>

        {/* Footer */}
        <div style={{ padding: "16px 24px", borderTop: "1px solid #e2e8f0", background: "#f8fafc", display: "flex", justifyContent: "flex-end", gap: 12 }}>
          <button
            onClick={onClose}
            style={{ padding: "8px 16px", background: "white", color: "#64748b", border: "1px solid #cbd5e1", borderRadius: 6, cursor: "pointer", fontWeight: 600 }}
          >
            ביטול
          </button>
          <button
            onClick={handleConfirm}
            style={{ padding: "8px 24px", background: "#059669", color: "white", border: "none", borderRadius: 6, cursor: "pointer", fontWeight: 600 }}
          >
            אשר והוסף קרנות
          </button>
        </div>
      </div>
    </div>
  );
}
